// frontend/src/context/AppContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { Spin, message } from 'antd';
import { useAuth } from './AuthContext';
import { ROLE_DEFINITIONS, ROLE_NAMES, ROLE_IDS, ROLES, PERMISSION_LEVELS } from '../const/roles';

const AppContext = createContext(null);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

const FullPageLoader = () => (
  <div className="fullscreen-loading" style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh', 
    width: '100vw', 
    position: 'fixed', 
    top: 0, 
    left: 0, 
    backgroundColor: 'rgba(255, 255, 255, 0.8)', 
    zIndex: 9999 
  }}>
    <div className="text-center">
      <Spin size="large" />
      <p className="text-lg mt-4">Loading application data...</p>
    </div>
  </div>
);

export const AppProvider = ({ children }) => {
  const { isAuthenticated, initialized: authInitialized, hasPermission } = useAuth();
  const [state, setState] = useState({
    appSettings: null,
    loading: false,
    error: null
  });

  const fetchSettings = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const response = await api.get('/api/settings');
      setState({
        appSettings: response.data.data,
        loading: false,
        error: null
      });
    } catch (err) {
      // Handle 403 specifically for permission issues
      if (err.response?.status === 403) {
        message.error('You do not have permission to access these settings');
      } else {
        message.error('Failed to load application settings');
      }
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: err 
      }));
    }
  }, []);

  useEffect(() => {
    if (authInitialized && isAuthenticated) {
      // Only fetch settings if user has permission
      if (hasPermission('settings', 'view', PERMISSION_LEVELS.ALL)) {
        fetchSettings();
      } else {
        setState(prev => ({ ...prev, appSettings: null }));
      }
    } else {
      setState(prev => ({ ...prev, appSettings: null }));
    }
  }, [authInitialized, isAuthenticated, hasPermission, fetchSettings]);

  const refreshAppSettings = useCallback(async () => {
    await fetchSettings();
  }, [fetchSettings]);

  const getSetting = useCallback((key) => {
    return state.appSettings?.[key];
  }, [state.appSettings]);

  const contextValue = useMemo(() => ({
    ...state,
    refreshAppSettings,
    getSetting
  }), [state, refreshAppSettings, getSetting]);

  if (state.loading) {
    return <FullPageLoader />;
  }

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};